import React, { useState, useEffect } from 'react';
import '../index.css';
import PaymentDetails from '../components/payments/PaymentDetails';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, GitBranch } from 'lucide-react';
import PaymentAuth from '../components/payments/PaymentAuth';
import { getPaymentDetails } from '../services/payment.service';
import { useAuth } from '../context/authContext';
import PaymentSummary from '../components/payments/PaymentSummary';
import PaymentAuthLogs from '../components/payments/PaymentAuthLogs';
import ApprovalTimeline from '../components/payments/ApprovalTimeline';
import PaymentStatus from '../components/payments/PaymentStatus';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { useResolvedPaymentParams } from '../hooks/useResolvedPaymentParams';
import {
    ApprovalTimelineCardSkeleton,
    PaymentAuthCardSkeleton,
    PaymentDetailsCardSkeleton,
    PaymentSummaryCardSkeleton,
} from '../components/ui/card-skeletons';
import { Skeleton } from '../components/ui/skeleton';
import AsyncContent from '../components/ui/AsyncContent';

const pageHeaderSkeleton = (
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-5 w-20 rounded-full" />
    </div>
);

const authSummarySkeleton = (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch">
        <PaymentAuthCardSkeleton />
        <PaymentSummaryCardSkeleton />
    </div>
);

const PaymentAuthPage = () => {
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updatePage, setUpdatePage] = useState(false);
    const { user, setUser } = useAuth();
    const { resolvedPaymentId, resolvedOrgId, isFromEmail } = useResolvedPaymentParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { paymentId } = useParams();

    // Email deep-links switch the active organisation so the rest of the portal
    // (and authorisation) have a real org context. Payments opened from the
    // "All Organisations" list keep that selection and use the payment's org
    // via resolvedOrgId for API calls only.
    useEffect(() => {
        if (isFromEmail && resolvedOrgId && String(resolvedOrgId) !== String(user.organisationId)) {
            const matchedOrg = user.organisations.find(org => String(org.id) === String(resolvedOrgId));
            if (matchedOrg) {
                setUser(prev => ({
                    ...prev,
                    organisationId: matchedOrg.id,
                    organisationName: matchedOrg.name,
                }));
            }
        }
    }, [isFromEmail, resolvedOrgId, user.organisationId, user.organisations, setUser]);

    const getBackLink = () => {
        if (location.state?.fromSearchParams) {
            return `/payments?${location.state.fromSearchParams}`;
        }
        return '/payments';
    };

    useEffect(() => {
        const fetchPaymentDetails = async () => {
            setLoading(true);
            const response = await getPaymentDetails(user.token, resolvedPaymentId, resolvedOrgId);
            if (response && response.status === 200) {
                setPaymentDetails(response.data.data);
            }
            setLoading(false);
        };

        fetchPaymentDetails();
    }, [resolvedPaymentId, resolvedOrgId, user.token, updatePage]);

    useEffect(() => {
        if (!isFromEmail || !resolvedPaymentId || paymentId === resolvedPaymentId) {
            return;
        }

        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('src');
        const nextSearch = searchParams.toString();

        navigate(
            {
                pathname: `/payments/${resolvedPaymentId}`,
                search: nextSearch ? `?${nextSearch}` : ''
            },
            {
                replace: true,
                state: location.state
            }
        );
    }, [isFromEmail, resolvedPaymentId, paymentId, location.search, location.state, navigate]);

    const pageSubtitle = paymentDetails
        ? (paymentDetails.payments?.length > 1
            ? `Bulk payment · ${paymentDetails.payments.length} payment lines`
            : paymentDetails.payments?.[0]?.reference || `Payment ${resolvedPaymentId}`)
        : null;

    return (
        <div className="App">
            <div className="max-w-8xl mx-auto flex flex-col gap-6">
                <Link
                    to={getBackLink()}
                    className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#2E2E2E] transition-colors w-fit"
                >
                    <ArrowLeft className="h-4 w-4" />
                    All payments
                </Link>

                <AsyncContent loading={loading} skeleton={pageHeaderSkeleton}>
                    {paymentDetails && (
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                            <div>
                                <h2 className="mb-1">Payment request</h2>
                                {pageSubtitle && (
                                    <p className="text-slate-500 text-sm m-0">{pageSubtitle}</p>
                                )}
                            </div>
                            <PaymentStatus status={paymentDetails.status} />
                        </div>
                    )}
                </AsyncContent>

                <AsyncContent loading={loading} skeleton={authSummarySkeleton}>
                    {paymentDetails && (
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch">
                            <PaymentAuth
                                paymentDetails={paymentDetails}
                                updatePage={updatePage}
                                setUpdatePage={setUpdatePage}
                            />
                            <PaymentSummary paymentDetails={paymentDetails} />
                        </div>
                    )}
                </AsyncContent>

                <div className="flex flex-col w-full gap-6">
                    <div className="flex flex-col lg:flex-row w-full gap-6 items-stretch">
                        <div className="flex-1 min-w-0 flex flex-col">
                            <PaymentAuthLogs updatePage={updatePage} />
                        </div>
                        {(loading || paymentDetails?.approval?.type === 'CUSTOM') && (
                            <div className="flex-1 min-w-0 flex flex-col">
                                <AsyncContent
                                    loading={loading}
                                    skeleton={<ApprovalTimelineCardSkeleton />}
                                >
                                    {paymentDetails?.approval?.type === 'CUSTOM' && (
                                        <Card className="h-full flex flex-col overflow-hidden">
                                            <CardHeader className="border-b border-slate-100 bg-slate-50/50 pb-4">
                                                <div className="flex items-center gap-3">
                                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-yavrio-green/10 border border-yavrio-green/20">
                                                        <GitBranch className="h-4 w-4 text-yavrio-green" />
                                                    </span>
                                                    <h3 className="my-0">Approval timeline</h3>
                                                </div>
                                            </CardHeader>
                                            <CardContent className="flex-1 pt-6">
                                                <ApprovalTimeline
                                                    approval={paymentDetails.approval}
                                                    paymentStatus={paymentDetails.status}
                                                />
                                            </CardContent>
                                        </Card>
                                    )}
                                </AsyncContent>
                            </div>
                        )}
                    </div>
                    <AsyncContent
                        loading={loading}
                        skeleton={<PaymentDetailsCardSkeleton />}
                    >
                        <PaymentDetails payment={paymentDetails} />
                    </AsyncContent>
                </div>
            </div>
        </div>
    );
};

export default PaymentAuthPage;
